/**
 * Document Comparison API Service
 *
 * Provides endpoints for creating, polling, and downloading results of
 * document comparisons between external CMS zips and internal Finch blobs.
 *
 * The create endpoint uses multipart/form-data to upload the zip directly.
 * Hand-written RTK Query slice (same pattern as crossMatterChangesService.ts).
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

// ---------------------------------------------------------------------------
// Types (matching backend app/defs/document_comparison.py)
// ---------------------------------------------------------------------------

export type DocumentComparisonStatus =
  | 'pending'
  | 'extracting'
  | 'hashing'
  | 'comparing'
  | 'building_results'
  | 'completed'
  | 'failed';

export interface DocumentComparisonSchema {
  id: string;
  matter_id: string;
  input_zip_s3_key: string | null;
  input_zip_filename: string | null;
  status: DocumentComparisonStatus;
  total_external_files: number;
  total_internal_files: number;
  processed_count: number;
  missing_internally_count: number;
  missing_externally_count: number;
  missing_internally_zip_key: string | null;
  missing_externally_zip_key: string | null;
  workflow_id: string | null;
  error_message: string | null;
  results: Record<string, unknown>;
  created_at: string;
  modified_at: string;
}

export interface DownloadResponse {
  download_url: string;
}

// ---------------------------------------------------------------------------
// API slice
// ---------------------------------------------------------------------------

export const documentComparisonApiClient = createApi({
  reducerPath: 'documentComparisonApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['DocumentComparison'],
  endpoints: (builder) => ({
    createDocumentComparison: builder.mutation<
      DocumentComparisonSchema,
      { matterId: string; file: File }
    >({
      query: ({ matterId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `api/matters/${matterId}/document-comparisons/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['DocumentComparison'],
    }),

    getDocumentComparison: builder.query<
      DocumentComparisonSchema,
      { matterId: string; comparisonId: string }
    >({
      query: ({ matterId, comparisonId }) => ({
        url: `api/matters/${matterId}/document-comparisons/${comparisonId}/`,
      }),
      providesTags: (_result, _error, { comparisonId }) => [
        { type: 'DocumentComparison', id: comparisonId },
      ],
    }),

    listDocumentComparisons: builder.query<
      DocumentComparisonSchema[],
      { matterId: string }
    >({
      query: ({ matterId }) => ({
        url: `api/matters/${matterId}/document-comparisons/`,
      }),
      transformResponse: (
        response: { comparisons: DocumentComparisonSchema[] },
      ) => response.comparisons,
      providesTags: ['DocumentComparison'],
    }),

    getDownloadMissingInternally: builder.query<
      DownloadResponse,
      { matterId: string; comparisonId: string }
    >({
      query: ({ matterId, comparisonId }) => ({
        url: `api/matters/${matterId}/document-comparisons/${comparisonId}/download-missing-internally/`,
      }),
    }),

    getDownloadMissingExternally: builder.query<
      DownloadResponse,
      { matterId: string; comparisonId: string }
    >({
      query: ({ matterId, comparisonId }) => ({
        url: `api/matters/${matterId}/document-comparisons/${comparisonId}/download-missing-externally/`,
      }),
    }),
  }),
});

export const {
  useCreateDocumentComparisonMutation,
  useGetDocumentComparisonQuery,
  useListDocumentComparisonsQuery,
  useLazyGetDownloadMissingInternallyQuery,
  useLazyGetDownloadMissingExternallyQuery,
} = documentComparisonApiClient;
